/**
 * Notification Service - In-app notifications for messages
 * Creates notifications in users/{userId}/notifications when messages are received
 */

import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

export interface MessageNotification {
  type: 'message';
  fromUserId: string;
  conversationId: string;
  preview: string;
  read: boolean;
  createdAt: any; // Firestore serverTimestamp
}

/**
 * Create an in-app notification for a new message
 * Called when a message is sent to notify all participants except the sender
 */
export const createMessageNotification = async (
  receiverId: string,
  senderId: string,
  conversationId: string,
  messagePreview: string
): Promise<void> => {
  if (receiverId === senderId) {
    // Don't notify the sender
    return;
  }

  try {
    const notificationsRef = collection(db, 'users', receiverId, 'notifications');
    
    const notification: Omit<MessageNotification, 'createdAt'> & { createdAt: any } = {
      type: 'message',
      fromUserId: senderId,
      conversationId: conversationId,
      preview: messagePreview.length > 100 
        ? messagePreview.substring(0, 100) + '...' 
        : messagePreview,
      read: false,
      createdAt: serverTimestamp()
    };

    await addDoc(notificationsRef, notification);
    console.log('[NOTIFICATIONS] ✅ Created message notification for user:', receiverId);
  } catch (error) {
    console.error('[NOTIFICATIONS] ❌ Error creating message notification:', error);
    // Don't throw - notification failure shouldn't break message sending
  }
};

/**
 * Mark a notification as read when user opens the conversation
 */
export const markNotificationAsRead = async (
  userId: string,
  notificationId: string
): Promise<void> => {
  try {
    const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
    await updateDoc(notificationRef, { read: true });
    console.log('[NOTIFICATIONS] ✅ Marked notification as read:', notificationId);
  } catch (error) {
    console.error('[NOTIFICATIONS] ❌ Error marking notification as read:', error);
  }
};

/**
 * Mark all message notifications for a conversation as read
 * Called when user opens a conversation
 */
export const markConversationNotificationsAsRead = async (
  userId: string,
  conversationId: string
): Promise<void> => {
  try {
    // Note: This requires reading all notifications and filtering client-side
    // For better performance, consider using a query with where('conversationId', '==', conversationId)
    // But Firestore rules may require reading all notifications first
    const { collection, query, where, getDocs, writeBatch } = await import('firebase/firestore');
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    
    // Query for unread message notifications for this conversation
    const q = query(
      notificationsRef,
      where('type', '==', 'message'),
      where('conversationId', '==', conversationId),
      where('read', '==', false)
    );

    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return; // No unread notifications
    }

    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => {
      batch.update(doc.ref, { read: true });
    });

    await batch.commit();
    console.log('[NOTIFICATIONS] ✅ Marked', snapshot.size, 'notifications as read for conversation:', conversationId);
  } catch (error) {
    console.error('[NOTIFICATIONS] ❌ Error marking conversation notifications as read:', error);
    // Don't throw - notification update failure shouldn't break conversation opening
  }
};

